import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DEPARTMENTS as departments } from "@/lib/departments";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BellRinging, Check } from "@phosphor-icons/react";

/**
 * Encart d'inscription aux alertes département par email.
 * Pas de compte requis.
 */
export default function SubscribeBox({ defaultDept = "", className = "" }) {
  const [email, setEmail] = useState("");
  const [dept, setDept] = useState(defaultDept);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !dept) { toast.error("Email et département requis"); return; }
    setLoading(true);
    try {
      await api.post("/subscriptions", { email, department: dept });
      setDone(true);
      toast.success("Abonnement enregistré ! Un email de confirmation est en route.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'abonnement");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className={`rounded-3xl bg-emerald-50 border-2 border-emerald-200 p-6 text-center ${className}`} data-testid="subscribe-success">
        <Check size={42} weight="duotone" className="text-emerald-500 mx-auto mb-2" />
        <h3 className="font-display text-xl font-black text-stone-800">Abonnement confirmé !</h3>
        <p className="text-sm text-stone-600 mt-1">Vous recevrez les alertes du département <strong>{dept}</strong> par email.</p>
      </div>
    );
  }

  return (
    <div className={`rounded-3xl bg-gradient-to-br from-rose-50 to-orange-50 border-2 border-rose-100 p-6 ${className}`} data-testid="subscribe-box">
      <div className="flex items-center gap-2 text-rose-600 font-bold text-sm uppercase tracking-wider">
        <BellRinging size={18} weight="duotone" /> Alertes par email
      </div>
      <h3 className="font-display text-xl sm:text-2xl font-black text-stone-800 mt-1">
        Recevez les alertes de votre département
      </h3>
      <p className="text-sm text-stone-600 mt-1.5 mb-4">
        Soyez prévenu·e par email à chaque doudou <strong>perdu</strong> ou <strong>trouvé</strong> près de chez vous. Un parent local repère plus vite qu'un parent à 800 km 🌟
      </p>
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
        <Input
          type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="vous@exemple.fr"
          className="rounded-full py-6 px-5 flex-1" data-testid="subscribe-email-input"
        />
        <select
          required value={dept} onChange={(e) => setDept(e.target.value)}
          className="rounded-full py-3 px-5 border border-stone-200 bg-white text-stone-700 font-semibold focus:outline-none focus:ring-2 focus:ring-orange-400 min-w-[170px]"
          data-testid="subscribe-dept-select"
        >
          <option value="">Département…</option>
          {departments.map((d) => (
            <option key={d.code} value={d.code}>{d.code} — {d.name}</option>
          ))}
        </select>
        <Button type="submit" disabled={loading} className="rounded-full bg-orange-500 hover:bg-orange-600 py-6 px-6 font-bold whitespace-nowrap shadow-lg shadow-orange-500/30" data-testid="subscribe-submit">
          {loading ? "..." : "S'abonner"}
        </Button>
      </form>
      <p className="text-xs text-stone-500 mt-3">
        🔒 Désabonnement en 1 clic depuis chaque email. Aucun spam, aucun partage de vos données.
      </p>
    </div>
  );
}
